import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, CheckCircle2, OctagonAlert, Scale } from "lucide-react";
import { MAT_META } from "@/lib/materials";
import { ADDITIVE_KEYS } from "@/lib/batching";
import { FlaskConical } from "lucide-react";
import { toast } from "sonner";

/* Rows: every material with a target or actual weight, deviation and per-material verdict. */
export function batchRows(batch) {
  const tol = Number(batch?.tolerance_pct ?? 1);
  const keys = [...new Set([...Object.keys(batch?.target_weights || {}), ...Object.keys(batch?.actual_weights || {})])];
  const order = Object.keys(MAT_META);
  keys.sort((a, b) => (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b)));
  return keys.map((k) => {
    const meta = MAT_META[k] || { label: k.replace(/_/g, " "), unit: "kg", color: "#71717A" };
    const target = Number(batch.target_weights?.[k] || 0);
    const actual = Number(batch.actual_weights?.[k] || 0);
    const delta = actual - target;
    const pct = target > 0 ? (delta / target) * 100 : 0;
    const captured = actual > 0;
    const flagged = (batch.out_of_tolerance || []).includes(k);
    const oot = flagged || (captured && target > 0 && Math.abs(pct) > tol);
    return { key: k, meta, target, actual, delta, pct, captured, oot };
  });
}

const fmt = (n, unit) => `${Number(n).toLocaleString(undefined, { maximumFractionDigits: unit === "kg" ? 1 : 0 })} ${unit}`;
const localTime = (iso) => (iso ? new Date(iso).toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function printBatch(batch, rows, mode) {
  const tol = Number(batch.tolerance_pct ?? 1);
  const ok = !!batch.within_tolerance;
  const failed = rows.filter((r) => r.oot);
  const html = `<!doctype html><html><head><title>Batch ${batch.mix_code} · ${batch.truck_number}</title>
<style>body{font-family:Helvetica,Arial,sans-serif;color:#18181B;margin:24px;font-size:12px}h1{font-size:18px;margin:0 0 4px}h2{font-size:13px;margin:18px 0 6px;text-transform:uppercase;letter-spacing:.08em;color:#71717A}
.meta{color:#52525B;margin-bottom:12px}.badge{display:inline-block;padding:6px 12px;border:2px solid ${ok ? "#16A34A" : "#DC2626"};color:${ok ? "#16A34A" : "#DC2626"};font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin:6px 0 12px}
table{width:100%;border-collapse:collapse}th,td{padding:5px 6px;border-bottom:1px solid #E4E4E7;text-align:right}th:first-child,td:first-child{text-align:left}th{font-size:10px;text-transform:uppercase;color:#71717A;letter-spacing:.06em}
.oot{color:#DC2626;font-weight:700}.ok{color:#16A34A;font-weight:700}.foot{margin-top:24px;color:#71717A;font-size:10px}.sig{margin-top:36px;display:flex;gap:40px}.sig div{flex:1;border-top:1px solid #18181B;padding-top:4px;font-size:10px;color:#52525B}</style></head><body>
<h1>BATCH RECORD · ${batch.mix_code || ""}</h1>
<div class="meta">Truck <b>${batch.truck_number || "—"}</b> · ${Number(batch.quantity_m3 || 0).toFixed(1)} m³ · Lane ${batch.lane_id || "—"} · Silo ${batch.cement_silo_id || "—"} · Completed ${localTime(batch.completed_at)} · Batch id ${batch.id}</div>
<div class="badge">${ok ? `Specs met · all materials within ±${tol}%` : `Out of tolerance · ${failed.length} material${failed.length === 1 ? "" : "s"} outside ±${tol}%`}</div>
${!ok && failed.length ? `<h2>Tolerance failures</h2><table><tr><th>Material</th><th>Target</th><th>Actual</th><th>Deviation</th><th>%</th></tr>${failed.map((r) => `<tr><td>${r.meta.label}</td><td>${fmt(r.target, r.meta.unit)}</td><td>${fmt(r.actual, r.meta.unit)}</td><td class="oot">${r.delta > 0 ? "+" : ""}${fmt(r.delta, r.meta.unit)}</td><td class="oot">${r.pct > 0 ? "+" : ""}${r.pct.toFixed(2)}%</td></tr>`).join("")}</table>` : ""}
<h2>Batch weights · target vs actual</h2>
<table><tr><th>Material</th><th>Target</th><th>Actual</th><th>Deviation</th><th>%</th><th>Status</th></tr>
${rows.map((r) => `<tr><td>${r.meta.label}</td><td>${fmt(r.target, r.meta.unit)}</td><td>${r.captured ? fmt(r.actual, r.meta.unit) : "—"}</td><td>${r.captured ? `${r.delta > 0 ? "+" : ""}${fmt(r.delta, r.meta.unit)}` : "—"}</td><td>${r.captured ? `${r.pct > 0 ? "+" : ""}${r.pct.toFixed(2)}%` : "—"}</td><td class="${r.oot ? "oot" : "ok"}">${r.captured ? (r.oot ? "OOT" : "OK") : "not captured"}</td></tr>`).join("")}</table>
<div class="sig"><div>Batcher signature</div><div>Date</div></div>
<div class="foot">Printed ${new Date().toLocaleString()} · ArmorBatch Global · tolerance ±${tol}% · ${mode === "tolerance" ? "Tolerance report" : "Mix breakdown"}</div>
<script>window.onload=function(){window.print();}</script></body></html>`;
  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) { toast.error("Pop-up blocked — allow pop-ups for this site to print the batch record"); return; }
  w.document.open(); w.document.write(html); w.document.close();
}

/* mode: "weights" (Mix Breakdown) | "tolerance" (OOT details / Specs Met certificate) */
export default function BatchDetailDialog({ batch, mode, onClose }) {
  if (!batch) return null;
  const rows = batchRows(batch);
  const tol = Number(batch.tolerance_pct ?? 1);
  const ok = !!batch.within_tolerance;
  const failed = rows.filter((r) => r.oot);
  const worst = rows.filter((r) => r.captured).reduce((m, r) => (Math.abs(r.pct) > Math.abs(m?.pct ?? -1) ? r : m), null);
  const title = mode === "tolerance" ? (ok ? "Specs Met · Tolerance Certificate" : "Out of Tolerance · Details") : `Mix Breakdown · ${batch.mix_code}`;
  const additiveRows = rows.filter((r) => ADDITIVE_KEYS.includes(r.key));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-3xl" data-testid="batch-detail-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase flex items-center gap-2" data-testid="batch-detail-title">
            {mode === "tolerance" ? (ok ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <OctagonAlert className="w-5 h-5 text-red-400" />) : <Scale className="w-5 h-5 text-primary-orange" />}
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial flex flex-wrap gap-x-3 gap-y-1 border-b border-industrial pb-2" data-testid="batch-detail-meta">
          <span>Mix <span className="text-primary-orange font-black">{batch.mix_code}</span></span>
          <span>Truck <span className="text-ink font-bold">{batch.truck_number || "—"}</span></span>
          <span>{Number(batch.quantity_m3 || 0).toFixed(1)} m³</span>
          <span>Lane {batch.lane_id || "—"}</span>
          <span>Silo {batch.cement_silo_id || "—"}</span>
          <span>Tol ±{tol}%</span>
          <span className="ml-auto">{localTime(batch.completed_at)}</span>
        </div>

        {mode === "tolerance" && ok && (
          <div className="border-2 border-emerald-500 bg-emerald-500/10 p-4 flex items-center gap-4" data-testid="specs-met-certificate">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 shrink-0" />
            <div>
              <div className="heading text-2xl font-black uppercase text-emerald-400 tracking-wide">Specs Met</div>
              <div className="text-sm text-ink">All {rows.filter((r) => r.captured).length} captured materials batched within ±{tol}% of the mix design.</div>
              {worst && <div className="mono text-[11px] text-muted-industrial mt-1">Largest deviation · {worst.meta.label} {worst.pct > 0 ? "+" : ""}{worst.pct.toFixed(2)}%</div>}
            </div>
          </div>
        )}
        {mode === "tolerance" && !ok && (
          <div className="border-2 border-red-600 bg-red-950/30 p-4" data-testid="oot-details">
            <div className="heading text-lg font-black uppercase text-red-400 flex items-center gap-2"><OctagonAlert className="w-5 h-5" /> {failed.length} material{failed.length === 1 ? "" : "s"} failed the ±{tol}% check</div>
            <ul className="mt-2 space-y-1.5">
              {failed.map((r) => (
                <li key={r.key} className="flex flex-wrap items-baseline gap-x-3 mono text-sm" data-testid={`oot-item-${r.key}`}>
                  <span className="font-black uppercase tracking-wide" style={{ color: r.meta.color }}>{r.meta.label}</span>
                  <span className="text-ink">{fmt(r.actual, r.meta.unit)} vs target {fmt(r.target, r.meta.unit)}</span>
                  <span className="text-red-400 font-black">{r.delta > 0 ? "+" : ""}{fmt(r.delta, r.meta.unit)} ({r.pct > 0 ? "+" : ""}{r.pct.toFixed(2)}%) · {Math.abs(r.pct) - tol > 0 ? `${(Math.abs(r.pct) - tol).toFixed(2)}% beyond limit` : "flagged by batcher"}</span>
                </li>
              ))}
              {failed.length === 0 && <li className="mono text-xs text-muted-industrial">Batch was flagged out of tolerance by the batcher but no material exceeds the limit in the captured weights.</li>}
            </ul>
          </div>
        )}

        {additiveRows.length > 0 && (
          <div className="border border-industrial bg-surface-2 p-3" data-testid="batch-additives-panel">
            <div className="flex items-center gap-2 mono text-[9px] uppercase tracking-widest text-muted-industrial">
              <FlaskConical className="w-3.5 h-3.5 text-primary-orange" /> Additives dosed · internal record
              <span className="ml-auto border border-amber-500 text-amber-400 px-1.5 py-0.5">Not shown on customer docket</span>
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {additiveRows.map((r) => (
                <div key={r.key} className="border border-industrial/60 px-2 py-1.5" data-testid={`batch-additive-${r.key}`}>
                  <div className="mono text-[9px] uppercase tracking-wide font-black" style={{ color: r.meta.color }}>{r.meta.label}</div>
                  <div className="mono text-sm font-black text-ink tabular-nums">{r.captured ? fmt(r.actual, "ml") : "—"}</div>
                  <div className={`mono text-[9px] tabular-nums ${r.oot ? "text-red-400" : "text-muted-industrial"}`}>target {fmt(r.target, "ml")}{r.captured ? ` · ${r.pct > 0 ? "+" : ""}${r.pct.toFixed(1)}%` : ""}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="max-h-[360px] overflow-y-auto">
          <table className="w-full mono text-[11px]" data-testid="batch-weights-table">
            <thead className="border-b border-industrial">
              <tr className="text-[9px] uppercase tracking-widest text-muted-industrial">
                <th className="text-left py-1.5 px-2">Material</th>
                <th className="text-right py-1.5 px-2">Target</th>
                <th className="text-right py-1.5 px-2">Actual</th>
                <th className="text-right py-1.5 px-2">Δ</th>
                <th className="text-right py-1.5 px-2">Δ %</th>
                <th className="text-right py-1.5 px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-industrial uppercase tracking-widest text-[10px]">No weights recorded for this batch</td></tr>}
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-industrial/30" data-testid={`batch-weight-row-${r.key}`}>
                  <td className="py-1.5 px-2 uppercase tracking-wide font-bold" style={{ color: r.meta.color }}>{r.meta.label}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-muted-industrial">{fmt(r.target, r.meta.unit)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-ink font-black">{r.captured ? fmt(r.actual, r.meta.unit) : "—"}</td>
                  <td className={`py-1.5 px-2 text-right tabular-nums ${r.oot ? "text-red-400" : "text-muted-industrial"}`}>{r.captured ? `${r.delta > 0 ? "+" : ""}${fmt(r.delta, r.meta.unit)}` : "—"}</td>
                  <td className={`py-1.5 px-2 text-right tabular-nums font-black ${r.oot ? "text-red-400" : r.captured ? "text-emerald-400" : "text-muted-industrial"}`}>{r.captured ? `${r.pct > 0 ? "+" : ""}${r.pct.toFixed(2)}%` : "—"}</td>
                  <td className={`py-1.5 px-2 text-right font-black ${r.oot ? "text-red-400" : r.captured ? "text-emerald-400" : "text-muted-industrial"}`}>{r.captured ? (r.oot ? "OOT" : "OK") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="batch-detail-close" className="rounded-none border-industrial uppercase text-xs">Close</Button>
          <Button onClick={() => printBatch(batch, rows, mode)} data-testid="batch-detail-print" className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase tracking-widest text-xs">
            <Printer className="w-3.5 h-3.5 mr-1" /> Print This Batch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
